import { setupI18n } from "@lingui/core";
import * as Sentry from "@sentry/tanstackstart-react";
import {
	type AnyRequestMiddleware,
	createMiddleware,
	createStart,
} from "@tanstack/react-start";
import { dynamicActivate } from "~/modules/lingui/i18n";
import { getLocaleFromRequest } from "~/modules/lingui/i18n.server";

Sentry.init({
	dsn: process.env.VITE_SENTRY_DSN,
	environment: process.env.VITE_ENVIRONMENT || "local",
	enabled: true,
	release: process.env.VITE_VERSION,

	integrations: [
		// Sentry.extraErrorDataIntegration({
		// 	depth: 5,
		// }),
		// Sentry.consoleLoggingIntegration(),
	],

	// Enable logs to be sent to Sentry
	enableLogs: true,

	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for tracing.
	// We recommend adjusting this value in production
	// Learn more at
	// https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
	tracesSampleRate: 1.0,

	// Set sampling rate for profiling - this is evaluated only once per SDK.init call
	profileSessionSampleRate: 1.0,

	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
});

const documentProfilingMiddleware = (): AnyRequestMiddleware => {
	return createMiddleware().server(async ({ next }) => {
		const result = await next();

		result.response.headers.set("Document-Policy", "js-profiling");

		return result;
	});
};

const linguiMiddleware = (): AnyRequestMiddleware => {
	return createMiddleware().server(async ({ request, next }) => {
		const { locale, headers } = getLocaleFromRequest(request);

		const i18n = setupI18n();
		await dynamicActivate(i18n, locale);

		const result = await next({
			context: {
				locale,
				i18n,
			},
		});

		if (headers) {
			headers.forEach(({ key, value }) => {
				result.response.headers.append(key, value);
			});
		}

		return result;
	});
};

export const startInstance = createStart(() => {
	return {
		requestMiddleware: [documentProfilingMiddleware(), linguiMiddleware()],
		functionMiddleware: [
			createMiddleware({ type: "function" }).server(
				Sentry.sentryGlobalServerMiddlewareHandler(),
			),
		],
	};
});
